import { ethers } from 'ethers';
import transactionService from './transactionService';

/**
 * Service for interacting with the Project Staking contract
 */
class ProjectStakingService {
  /**
   * Create a new ProjectStakingService instance
   * @param {Object} contract - Ethers.js contract instance
   * @param {Object} celTokenContract - CEL Token contract instance
   */
  constructor(contract, celTokenContract) {
    this.contract = contract;
    this.celToken = celTokenContract;
  }

  /**
   * Get staking parameters
   * @returns {Promise<Object>} - Object containing minimum lock duration, maximum lock duration, and multiplier factor
   */
  async getStakingParameters() {
    try {
      // Using the correct contract functions from ProjectStaking.sol
      const minLockDuration = await this.contract.minLockDuration();
      const maxLockDuration = await this.contract.maxLockDuration();
      const multiplierFactor = await this.contract.multiplierFactor();
      const scorePrecision = await this.contract.SCORE_PRECISION();
      
      // Get penalty rates for early unstaking
      let minPenaltyRate = 500; // Default 5%
      let maxPenaltyRate = 5000; // Default 50%
      
      try {
        minPenaltyRate = await this.contract.minPenaltyRate();
        maxPenaltyRate = await this.contract.maxPenaltyRate();
        console.log(`Penalty rates: min=${minPenaltyRate}, max=${maxPenaltyRate}`);
      } catch (error) {
        console.log('Could not fetch penalty rates, using defaults', error);
      }
      
      return {
        minLockDuration: minLockDuration.toNumber(),
        maxLockDuration: maxLockDuration.toNumber(),
        multiplierFactor: multiplierFactor.toNumber(),
        scorePrecision: scorePrecision.toNumber(),
        // Convert to days for display
        minLockDays: Math.floor(minLockDuration.toNumber() / (24 * 60 * 60)),
        maxLockDays: Math.floor(maxLockDuration.toNumber() / (24 * 60 * 60)),
        // Include penalty rates
        minPenaltyRate: minPenaltyRate,
        maxPenaltyRate: maxPenaltyRate
      };
    } catch (error) {
      console.error('Error getting staking parameters:', error);
      throw error;
    }
  }

  /**
   * Get active stakes for a user in a specific project
   * @param {number} projectId - Project ID
   * @param {string} userAddress - User address
   * @returns {Promise<Array>} - Array of stake objects with details
   */
  async getUserActiveStakes(projectId, userAddress) {
    try {
      console.log(`Getting active stakes for project ${projectId} and user ${userAddress}`);
      
      // Call the contract method
      console.log(`Calling contract.getUserActiveStakes for project ${projectId} and user ${userAddress}`);
      const result = await this.contract.getUserActiveStakes(projectId, userAddress);
      console.log(`Raw stake data from contract for project ${projectId}: `, result);
      
      // If result is undefined or null, return empty array
      if (!result) {
        console.log(`No stake data returned for project ${projectId}`);
        return [];
      }
      
      // Check if result is an array (some ethers.js versions may return arrays differently)
      console.log(`Stake data is an array with ${result.length} items`);
      
      // Extract data from the result
      // The contract returns multiple arrays: [indexes, amounts, startTimes, unlockTimes, lockDurations, scores]
      const indexes = result.indexes || result[0];
      const amounts = result.amounts || result[1];
      const startTimes = result.startTimes || result[2];
      const unlockTimes = result.unlockTimes || result[3];
      const lockDurations = result.lockDurations || result[4];
      const scores = result.scores || result[5];
      
      // Create stake objects by combining the arrays
      const stakes = [];
      for (let i = 0; i < indexes.length; i++) {
        // Check if amount is zero (this can happen if stakes have been unstaked)
        if (amounts[i].isZero()) {
          console.log(`Stake with zero amount found, filtering out: ${amounts[i]}`);
          continue;
        }
        
        const now = Math.floor(Date.now() / 1000);
        const canUnstake = now >= unlockTimes[i].toNumber();
        
        stakes.push({
          stakeIndex: indexes[i].toNumber(),
          amount: amounts[i],
          formattedAmount: ethers.utils.formatEther(amounts[i]),
          startTime: startTimes[i].toNumber(),
          unlockTime: unlockTimes[i].toNumber(),
          lockDuration: lockDurations[i].toNumber(),
          score: scores[i],
          canUnstake,
          timeUntilUnlock: this.formatTimeUntilUnlock(unlockTimes[i].toNumber()),
          formattedLockDuration: this.formatLockDuration(lockDurations[i].toNumber())
        });
      }
      
      console.log(`Processed ${stakes.length} stakes from raw data`);
      return stakes;
    } catch (error) {
      // Check if it's a "project does not exist" error
      if (error.message && error.message.includes("Project does not exist")) {
        console.log(`Project ${projectId} does not exist`);
        return [];
      }
      
      console.error(`Error getting active stakes for project ${projectId}:`, error);
      throw error;
    }
  }

  /**
   * Get all active stakes for a user across all projects
   * @param {string} userAddress - User address
   * @returns {Promise<Array>} - Array of objects containing project ID and stake details
   */
  async getAllUserStakesAcrossProjects(userAddress) {
    try {
      console.log(`Getting all active stakes across all projects for user ${userAddress}`);
      
      // First, get all the projects where the user has active stakes using the new function
      const projectIds = await this.getUserStakedProjects(userAddress);
      console.log(`Found ${projectIds.length} projects with active stakes for user ${userAddress}:`, projectIds);
      
      if (projectIds.length === 0) {
        return [];
      }
      
      // For each project, get the active stakes
      const allProjectStakes = await Promise.all(
        projectIds.map(async (projectId) => {
          try {
            // Get active stakes for this project
            const stakeObjects = await this.getUserActiveStakes(projectId, userAddress);
            
            if (stakeObjects && stakeObjects.length > 0) {
              // Try to get project name if possible
              let projectName = `Project #${projectId}`;
              try {
                const innovationUnits = window.services?.innovationUnits;
                if (innovationUnits) {
                  const details = await innovationUnits.getProjectDetails(projectId);
                  if (details && details.name) {
                    projectName = details.name;
                  }
                }
              } catch (nameError) {
                console.log(`Could not get project name for project ${projectId}:`, nameError);
              }
              
              return {
                projectId,
                projectName,
                stakes: stakeObjects
              };
            }
            return null;
          } catch (error) {
            console.error(`Error getting stakes for project ${projectId}:`, error);
            return null;
          }
        })
      );
      
      // Filter out null values
      const validProjectStakes = allProjectStakes.filter(project => project !== null);
      console.log(`Successfully retrieved stakes from ${validProjectStakes.length} projects`);
      
      return validProjectStakes;
    } catch (error) {
      console.error('Error getting all user stakes across projects:', error);
      throw error;
    }
  }

  /**
   * Get total staked amount for a project
   * @param {number} projectId - Project ID
   * @returns {Promise<BigNumber>} - Total staked amount
   */
  async getTotalStakedAmount(projectId) {
    try {
      return await this.contract.getTotalStakedAmount(projectId);
    } catch (error) {
      console.error('Error getting total staked amount:', error);
      throw error;
    }
  }

  /**
   * Get total staking score for a project
   * @param {number} projectId - Project ID
   * @returns {Promise<BigNumber>} - Total staking score
   */
  async getTotalStakingScore(projectId) {
    try {
      return await this.contract.getTotalStakingScore(projectId);
    } catch (error) {
      console.error('Error getting total staking score:', error);
      throw error;
    }
  }

  /**
   * Get total staked amount for a user on a project
   * @param {number} projectId - Project ID
   * @param {string} userAddress - User address
   * @returns {Promise<BigNumber>} - Total staked amount
   */
  async getUserTotalStakedAmount(projectId, userAddress) {
    try {
      console.log(`Getting total staked amount for project ${projectId} and user ${userAddress}`);
      
      // Try to use getUserActiveStakes to get all active stakes and sum them
      try {
        const activeStakes = await this.getUserActiveStakes(projectId, userAddress);
        console.log(`Found ${activeStakes.length} active stakes for project ${projectId}`);
        
        // Sum up all stake amounts
        let totalAmount = ethers.constants.Zero;
        for (const stake of activeStakes) {
          totalAmount = totalAmount.add(stake.amount);
        }
        
        console.log(`Total staked amount for project ${projectId}: ${ethers.utils.formatEther(totalAmount)} CEL`);
        return totalAmount;
      } catch (error) {
        console.log('getUserActiveStakes failed, trying alternate method:', error);
      }
      
      // Method 2: Try using the public mapping directly (if it's exposed as a view function)
      try {
        console.log('Trying projectUserTotalStaked mapping...');
        const amount = await this.contract.projectUserTotalStaked(projectId, userAddress);
        console.log(`projectUserTotalStaked returned: ${ethers.utils.formatEther(amount)} CEL`);
        return amount;
      } catch (error) {
        console.log('projectUserTotalStaked failed, trying another method:', error);
      }
      
      // Method 3: Use a more direct approach - check if stake info is available
      try {
        console.log('Trying userProjectStakeIndexes method...');
        const stakeIndexes = await this.contract.userProjectStakeIndexes(userAddress, projectId);
        if (stakeIndexes && stakeIndexes.length > 0) {
          console.log(`Found ${stakeIndexes.length} stake indexes`);
          // User has stakes, but we can't determine amount without more info
          // Return a small amount to indicate "has stakes"
          return ethers.utils.parseEther("0.000001");
        }
      } catch (error) {
        console.log('userProjectStakeIndexes failed:', error);
      }
      
      // No stakes found with any method
      console.log(`No stakes found for project ${projectId}`);
      return ethers.constants.Zero;
    } catch (error) {
      console.error('Error getting user total staked amount:', error);
      return ethers.constants.Zero; // Return zero instead of throwing for better UX
    }
  }

  /**
   * Calculate staking score for a given amount and lock time
   * @param {BigNumber} amount - Amount to stake
   * @param {number} lockDuration - Lock duration in seconds
   * @returns {Promise<BigNumber>} - Staking score
   */
  async calculateStakingScore(amount, lockDuration) {
    try {
      return await this.contract.calculateStakingScore(amount, lockDuration);
    } catch (error) {
      console.error('Error calculating staking score:', error);
      throw error;
    }
  }

  /**
   * Stake tokens for a project
   * @param {number} projectId - Project ID
   * @param {BigNumber} amount - Amount to stake
   * @param {number} lockDurationDays - Lock duration in days
   * @returns {Promise<Object>} - Transaction result with stake index
   */
  async stake(projectId, amount, lockDurationDays) {
    console.log(`Starting stake process for project ${projectId} with amount ${ethers.utils.formatEther(amount)} CEL and lock duration ${lockDurationDays} days`);
    
    try {
      // First, fetch the min/max lock durations from the contract to check bounds
      try {
        const minLockDuration = await this.contract.minLockDuration();
        const maxLockDuration = await this.contract.maxLockDuration();
        
        console.log('Contract lock duration bounds:');
        console.log('- Min lock duration (seconds):', minLockDuration.toString());
        console.log('- Min lock duration (days):', minLockDuration.div(86400).toString());
        console.log('- Max lock duration (seconds):', maxLockDuration.toString());
        console.log('- Max lock duration (days):', maxLockDuration.div(86400).toString());
        
        // Calculate what the contract will convert our days to
        const lockDurationInSeconds = ethers.BigNumber.from(lockDurationDays).mul(86400);
        console.log(`Our lock duration ${lockDurationDays} days will convert to ${lockDurationInSeconds.toString()} seconds in the contract`);
        
        // Check if our duration is within bounds
        const isWithinBounds = 
          lockDurationInSeconds.gte(minLockDuration) && 
          lockDurationInSeconds.lte(maxLockDuration);
        
        console.log(`Is our duration within bounds? ${isWithinBounds ? 'YES' : 'NO!!!'}`);
        
        if (!isWithinBounds) {
          console.error(`DURATION OUT OF BOUNDS: ${lockDurationDays} days (${lockDurationInSeconds.toString()} seconds) is outside allowed range of ${minLockDuration.toString()}-${maxLockDuration.toString()} seconds`);
        }
      } catch (error) {
        console.error('Error checking lock duration bounds:', error);
      }
      
      // Debug: Log CEL token service availability
      console.log('CEL Token service available:', !!this.celToken, this.celToken);
      
      // First check if user has enough tokens
      const walletAddress = await this.contract.signer.getAddress();
      console.log('User wallet address:', walletAddress);
      
      const userBalance = await this.celToken.balanceOf(walletAddress);
      console.log('User balance:', ethers.utils.formatEther(userBalance), 'CEL');
      
      if (userBalance.lt(amount)) {
        throw new Error(`Insufficient token balance. You have ${ethers.utils.formatEther(userBalance)} CEL but trying to stake ${ethers.utils.formatEther(amount)} CEL.`);
      }
      
      // Get the contract address for token approval
      const contractAddress = this.contract.address;
      console.log('Contract address for approval:', contractAddress);
      
      // First, check the current allowance
      const currentAllowance = await this.celToken.allowance(walletAddress, contractAddress);
      console.log('Current allowance:', ethers.utils.formatEther(currentAllowance), 'CEL');
      
      // If allowance is insufficient, approve tokens
      if (currentAllowance.lt(amount)) {
        console.log('Approving tokens:', ethers.utils.formatEther(amount), 'CEL');
        // Use the approve method directly since ensureAllowance might be causing issues
        const approvalTx = await this.celToken.approve(contractAddress, amount);
        console.log('Approval transaction:', approvalTx);
        
        // Wait for approval transaction to be mined
        const approvalReceipt = await approvalTx.wait();
        console.log('Approval receipt:', approvalReceipt);
        
        if (!approvalReceipt.status) {
          throw new Error('Token approval failed. Please try again.');
        }
      } else {
        console.log('Token allowance already sufficient');
      }
      
      // Now stake tokens with the specified lock duration in seconds
      // Convert days to seconds for the contract
      const lockDurationSeconds = lockDurationDays * 24 * 60 * 60;
      console.log(`Staking with lock duration of ${lockDurationSeconds} seconds (${lockDurationDays} days)`);
      console.log(`Project ID used for staking: ${projectId} (type: ${typeof projectId})`);
      
      // Call the contract's stake function
      try {
        // Convert days to an integer just to be safe
        const lockDurationDaysInt = parseInt(lockDurationDays);
        console.log(`Passing lock duration as days: ${lockDurationDaysInt} days (contract will convert to seconds)`);
        
        const transaction = await this.contract.stake(projectId, amount, lockDurationDaysInt);
        console.log('Stake transaction initiated:', transaction);
        
        // Wait for transaction to be mined
        const receipt = await transaction.wait();
        console.log('Stake transaction receipt:', receipt);
        
        // Extract stake index from the event
        let stakeIndex = 0;
        if (receipt && receipt.events) {
          // Look for events with different possible names (Staked, ProjectStaked, Stake)
          const stakeEvent = receipt.events.find(e => 
            e.event === 'ProjectStaked' || 
            e.event === 'Staked' || 
            e.event === 'Stake'
          );
          
          if (stakeEvent && stakeEvent.args) {
            // Log the full event data to help debug
            console.log('Found staking event:', stakeEvent.event, stakeEvent.args);
            
            // Try to extract stake index from different possible argument names
            if (stakeEvent.args.stakeIndex !== undefined) {
              stakeIndex = stakeEvent.args.stakeIndex.toNumber();
            } else if (stakeEvent.args.index !== undefined) {
              stakeIndex = stakeEvent.args.index.toNumber();
            } else if (stakeEvent.args[0] !== undefined && typeof stakeEvent.args[0].toNumber === 'function') {
              // If the first argument is a number, assume it's the index
              stakeIndex = stakeEvent.args[0].toNumber();
            }
            
            console.log('Stake index extracted:', stakeIndex);
          } else {
            // If no specific event found, log all events to help debug
            console.log('All transaction events:', receipt.events);
          }
        }
        
        return {
          success: true,
          stakeIndex,
          transactionHash: receipt.transactionHash
        };
      } catch (error) {
        console.error('Error in stake transaction:', error);
        
        // Handle specific error types
        if (error.message && error.message.includes("Project does not exist")) {
          throw new Error(`Project ID ${projectId} does not exist on this blockchain network. Please check project ID and network.`);
        } else if (error.data && error.data.message && error.data.message.includes("Project does not exist")) {
          throw new Error(`Project ID ${projectId} does not exist on this blockchain network. Please check project ID and network.`);
        } else if (error.code === 'UNPREDICTABLE_GAS_LIMIT' && error.error && error.error.data && error.error.data.message) {
          // Extract the reason from the error data
          throw new Error(`Contract error: ${error.error.data.message}`);
        }
        
        // Otherwise rethrow the original error
        throw error;
      }
    } catch (error) {
      console.error('Error staking tokens:', error);
      throw error;
    }
  }

  /**
   * Unstake tokens from a project
   * @param {number} projectId - Project ID
   * @param {number} stakeIndex - Stake index
   * @returns {Promise<Object>} - Transaction result
   */
  async unstake(projectId, stakeIndex) {
    try {
      const tx = this.contract.unstake(projectId, stakeIndex);
      return await transactionService.sendTransaction(tx, {
        title: 'Unstake CEL',
        description: `Unstaking CEL from project ${projectId}`,
        type: 'unstake_cel'
      });
    } catch (error) {
      console.error('Error unstaking tokens:', error);
      throw error;
    }
  }

  /**
   * Get unclaimed staking rewards for a user
   * @param {number} projectId - Project ID
   * @param {string} userAddress - User address
   * @returns {Promise<BigNumber>} - Unclaimed staking rewards
   */
  async getUnclaimedStakingRewards(projectId, userAddress) {
    try {
      return await this.contract.getUnclaimedStakingRewards(projectId, userAddress);
    } catch (error) {
      console.error('Error getting unclaimed staking rewards:', error);
      throw error;
    }
  }

  /**
   * Claim staking rewards for a project
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} - Transaction result
   */
  async claimStakingRewards(projectId) {
    try {
      const tx = this.contract.claimStakingRewards(projectId);
      return await transactionService.sendTransaction(tx, {
        title: 'Claim Staking Rewards',
        description: `Claiming staking rewards for project ${projectId}`,
        type: 'claim_staking_rewards'
      });
    } catch (error) {
      console.error('Error claiming staking rewards:', error);
      throw error;
    }
  }

  /**
   * Format lock duration for display
   * @param {number} durationInSeconds - Duration in seconds
   * @returns {string} - Formatted duration
   */
  formatLockDuration(durationInSeconds) {
    const days = Math.floor(durationInSeconds / (60 * 60 * 24));
    
    if (days === 1) {
      return '1 day';
    } else if (days < 30) {
      return `${days} days`;
    } else if (days < 365) {
      const months = Math.floor(days / 30);
      return months === 1 ? '1 month' : `${months} months`;
    } else {
      const years = Math.floor(days / 365);
      const remainingMonths = Math.floor((days % 365) / 30);
      
      let result = years === 1 ? '1 year' : `${years} years`;
      if (remainingMonths > 0) {
        result += remainingMonths === 1 ? ' and 1 month' : ` and ${remainingMonths} months`;
      }
      
      return result;
    }
  }

  /**
   * Format time until unlock for display
   * @param {number} unlockTime - Unlock timestamp in seconds
   * @returns {string} - Formatted time until unlock
   */
  formatTimeUntilUnlock(unlockTime) {
    const now = Math.floor(Date.now() / 1000);
    
    if (unlockTime <= now) {
      return 'Unlocked';
    }
    
    const secondsRemaining = unlockTime - now;
    return this.formatLockDuration(secondsRemaining);
  }

  /**
   * Get all projects where a user has active stakes
   * @param {string} userAddress - User's wallet address
   * @returns {Promise<number[]>} - Array of project IDs
   */
  async getUserStakedProjects(userAddress) {
    try {
      console.log(`Getting projects where user ${userAddress} has active stakes`);
      
      try {
        // Try to use the new direct contract method first
        console.log('Using direct getUserStakedProjects contract method');
        const projects = await this.contract.getUserStakedProjects(userAddress);
        console.log(`Direct contract call returned ${projects.length} projects:`, projects);
        
        // Convert BigNumber objects to plain numbers
        return projects.map(projectId => (
          typeof projectId.toNumber === 'function' ? projectId.toNumber() : Number(projectId)
        ));
      } catch (error) {
        console.error('Error calling getUserStakedProjects from contract:', error);
        
        // Fall back to the old scanning method
        console.log('Falling back to scanning method...');
        
        // Define a list of potential projects to check
        // Start with a reasonable range that covers most projects
        const projectsToCheck = [];
        
        // Get the total number of projects if the function exists
        try {
          const totalProjects = await this.contract.totalProjects();
          console.log(`Total projects from contract: ${totalProjects}`);
          
          // Add projects from 1 to totalProjects (or up to a reasonable limit)
          const maxToCheck = Math.min(totalProjects.toNumber(), 50);
          for (let i = 1; i <= maxToCheck; i++) {
            projectsToCheck.push(i);
          }
        } catch (error) {
          console.log('Contract does not have totalProjects function, using predefined list');
          
          // If totalProjects is not available, check common project IDs
          // Add projects 1-10 as a reasonable default
          for (let i = 1; i <= 10; i++) {
            projectsToCheck.push(i);
          }
        }
        
        console.log(`Checking ${projectsToCheck.length} potential projects for stakes`);
        
        // To avoid too many concurrent requests, process in batches
        const batchSize = 5;
        const stakedProjects = [];
        
        for (let i = 0; i < projectsToCheck.length; i += batchSize) {
          const batch = projectsToCheck.slice(i, i + batchSize);
          
          // Process each batch in parallel
          const results = await Promise.all(
            batch.map(async (projectId) => {
              try {
                // Check if the user has any active stakes for this project
                const stakes = await this.getUserActiveStakes(projectId, userAddress);
                
                // If any valid stakes are found, return the project ID
                if (stakes && stakes.length > 0) {
                  console.log(`Found ${stakes.length} active stakes in project ${projectId}`);
                  return projectId;
                }
                return null;
              } catch (error) {
                // Skip projects that cause errors
                console.log(`Skipping project ${projectId} due to error: ${error.message}`);
                return null;
              }
            })
          );
          
          // Add valid project IDs to the result
          stakedProjects.push(...results.filter(id => id !== null));
        }
        
        console.log(`Total staked projects found: ${stakedProjects.length} \n${stakedProjects}`);
        return stakedProjects;
      }
    } catch (error) {
      console.error('Error in getUserStakedProjects:', error);
      return []; // Return empty array on error
    }
  }

  /**
   * Get all active stakes for a user across all projects
   * @param {string} userAddress - User address
   * @returns {Promise<Object>} - Object containing arrays of project IDs, stake indexes, amounts, and unlock times
   */
  async getAllUserStakes(userAddress) {
    try {
      console.log(`Getting all stakes for user ${userAddress} using getAllUserStakes contract method`);
      return await this.contract.getAllUserStakes(userAddress);
    } catch (error) {
      console.error('Error getting all user stakes:', error);
      throw error;
    }
  }

  /**
   * Check if a project exists and is available for staking
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} - Project staking info
   */
  async checkProjectStakeable(projectId) {
    try {
      console.log(`Checking if project ${projectId} is stakeable`);
      
      // Try to get project details directly
      try {
        // Check if the project exists by calling a view function that would fail if project doesn't exist
        const totalStaked = await this.contract.getTotalStakedAmount(projectId);
        console.log(`Project ${projectId} total staked: ${ethers.utils.formatEther(totalStaked)} CEL`);
        
        const totalScore = await this.contract.getTotalStakingScore(projectId);
        console.log(`Project ${projectId} total score: ${totalScore.toString()}`);
        
        // Project exists and is stakeable
        return {
          exists: true,
          totalStaked: totalStaked,
          totalScore: totalScore,
          formattedTotalStaked: ethers.utils.formatEther(totalStaked),
          message: `Project ${projectId} is stakeable with ${ethers.utils.formatEther(totalStaked)} CEL total staked`
        };
      } catch (error) {
        // Check if error contains specific message about project not existing
        if (
          (error.message && error.message.includes("Project does not exist")) ||
          (error.data && error.data.message && error.data.message.includes("Project does not exist"))
        ) {
          console.log(`Project ${projectId} does not exist`);
          return {
            exists: false,
            message: `Project ${projectId} does not exist or is not stakeable`
          };
        }
        
        // Other error, may still be stakeable
        console.error(`Error checking if project ${projectId} is stakeable:`, error);
        return {
          exists: false,
          error: error.message,
          message: `Error checking project ${projectId}: ${error.message}`
        };
      }
    } catch (error) {
      console.error(`Error in checkProjectStakeable for project ${projectId}:`, error);
      return {
        exists: false,
        error: error.message,
        message: `General error checking project ${projectId}: ${error.message}`
      };
    }
  }

  /**
   * Get the project staking score
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} - Project staking score with formatted value
   */
  async getProjectStakingScore(projectId) {
    try {
      console.log(`Getting staking score for project ${projectId}`);
      
      // Call the contract function to get project score
      const score = await this.contract.getProjectScore(projectId);
      console.log(`Project ${projectId} staking score: ${score.toString()}`);
      
      return {
        raw: score,
        formatted: ethers.utils.formatEther(score)
      };
    } catch (error) {
      // Check if it's a "project does not exist" error
      if (error.message && error.message.includes("Project does not exist")) {
        console.log(`Project ${projectId} does not exist`);
        return {
          raw: ethers.constants.Zero,
          formatted: '0'
        };
      }
      
      console.error(`Error getting staking score for project ${projectId}:`, error);
      return {
        raw: ethers.constants.Zero,
        formatted: '0'
      };
    }
  }

  /**
   * Get the user's staking score for a specific project
   * @param {number} projectId - Project ID
   * @param {string} userAddress - User's wallet address
   * @returns {Promise<Object>} - User's staking score with formatted value
   */
  async getUserProjectScore(projectId, userAddress) {
    try {
      console.log(`Getting staking score for user ${userAddress} on project ${projectId}`);
      
      // Call the contract function to get user's project score
      const score = await this.contract.getUserProjectScore(projectId, userAddress);
      console.log(`User ${userAddress} staking score for project ${projectId}: ${score.toString()}`);
      
      return {
        raw: score,
        formatted: ethers.utils.formatEther(score)
      };
    } catch (error) {
      // Check if it's a "project does not exist" error
      if (error.message && error.message.includes("Project does not exist")) {
        console.log(`Project ${projectId} does not exist`);
        return {
          raw: ethers.constants.Zero,
          formatted: '0'
        };
      }
      
      console.error(`Error getting user staking score for project ${projectId}:`, error);
      return {
        raw: ethers.constants.Zero,
        formatted: '0'
      };
    }
  }

  /**
   * Get the total staking score across all projects for a user
   * @param {string} userAddress - User's wallet address
   * @returns {Promise<Object>} - User's total staking score with formatted value
   */
  async getUserTotalScore(userAddress) {
    try {
      console.log(`Getting total staking score for user ${userAddress} across all projects`);
      
      // Call the contract function to get user's total score
      const score = await this.contract.getUserTotalScore(userAddress);
      console.log(`User ${userAddress} total staking score: ${score.toString()}`);
      
      return {
        raw: score,
        formatted: ethers.utils.formatEther(score)
      };
    } catch (error) {
      console.error(`Error getting total staking score for user ${userAddress}:`, error);
      return {
        raw: ethers.constants.Zero,
        formatted: '0'
      };
    }
  }

  /**
   * Get the total staked amount for a project
   * @param {number} projectId - Project ID 
   * @returns {Promise<Object>} - Total staked amount with formatted value
   */
  async getProjectTotalStakedAmount(projectId) {
    try {
      console.log(`Getting total staked amount for project ${projectId}`);
      
      // Get the total staked amount from the contract
      const totalStaked = await this.contract.projectTotalStaked(projectId);
      console.log(`Project ${projectId} total staked: ${ethers.utils.formatEther(totalStaked)} CEL`);
      
      return {
        raw: totalStaked,
        formatted: ethers.utils.formatEther(totalStaked)
      };
    } catch (error) {
      // Check if it's a "project does not exist" error
      if (error.message && error.message.includes("Project does not exist")) {
        console.log(`Project ${projectId} does not exist`);
        return {
          raw: ethers.constants.Zero,
          formatted: '0'
        };
      }
      
      console.error(`Error getting total staked amount for project ${projectId}:`, error);
      return {
        raw: ethers.constants.Zero,
        formatted: '0'
      };
    }
  }

  /**
   * Get all stakers for a project along with their staked amounts
   * @param {number|string} projectId - The project ID
   * @returns {Promise<Object>} - Arrays of staker addresses and their staked amounts
   */
  async getProjectStakers(projectId) {
    try {
      console.log(`Getting all stakers for project ${projectId}`);
      
      // Call the contract method to get all stakers for this project
      const result = await this.contract.getProjectStakers(projectId);
      const { 0: stakers, 1: amounts } = result;
      
      console.log(`Found ${stakers.length} stakers for project ${projectId}`);
      
      // Format the amounts from wei to ether
      const formattedAmounts = amounts.map(amount => 
        ethers.utils.formatEther(amount)
      );
      
      return {
        stakers: stakers,
        amounts: formattedAmounts,
        raw: {
          stakers: stakers,
          amounts: amounts
        }
      };
    } catch (error) {
      console.error(`Error getting stakers for project ${projectId}:`, error);
      return {
        stakers: [],
        amounts: [],
        raw: {
          stakers: [],
          amounts: []
        }
      };
    }
  }

  /**
   * Early unstake tokens with a penalty
   * @param {number} projectId - Project ID
   * @param {number} stakeIndex - Stake index
   * @returns {Promise<Object>} - Transaction result with penalty amount
   */
  async earlyUnstake(projectId, stakeIndex) {
    try {
      console.log(`Initiating early unstake for project ${projectId}, stake index ${stakeIndex}`);
      
      // Call the contract to estimate the penalty amount first (gasLimit: false to just read)
      let estimatedPenalty;
      try {
        // Try to estimate the penalty by calling with static (this is a read-only call that won't send a transaction)
        estimatedPenalty = await this.contract.callStatic.earlyUnstake(projectId, stakeIndex);
        console.log(`Estimated penalty for early unstake: ${ethers.utils.formatEther(estimatedPenalty)} CEL`);
      } catch (error) {
        console.error('Error estimating penalty:', error);
        // If we can't estimate, we'll proceed without the estimate
      }

      // Execute the actual transaction
      const tx = this.contract.earlyUnstake(projectId, stakeIndex);
      const result = await transactionService.sendTransaction(tx, {
        title: 'Early Unstake CEL',
        description: `Early unstaking CEL from project ${projectId} with penalty`,
        type: 'early_unstake_cel'
      });
      
      // If we have the estimated penalty, include it in the result
      if (estimatedPenalty) {
        result.penaltyAmount = estimatedPenalty;
        result.formattedPenaltyAmount = ethers.utils.formatEther(estimatedPenalty);
      }
      
      return result;
    } catch (error) {
      console.error('Error early unstaking tokens:', error);
      throw error;
    }
  }

  /**
   * Calculate the estimated penalty for early unstaking
   * @param {number} projectId - Project ID
   * @param {number} stakeIndex - Stake index
   * @returns {Promise<Object>} - Object with penalty amount and rate
   */
  async calculateEarlyUnstakePenalty(projectId, stakeIndex) {
    try {
      console.log(`Calculating early unstake penalty for project ${projectId}, stake index ${stakeIndex}`);
      
      // Get the stake information
      const userAddress = await this.contract.signer.getAddress();
      console.log(`Getting stakes for user ${userAddress}`);
      const stakes = await this.getUserActiveStakes(projectId, userAddress);
      console.log(`Retrieved ${stakes.length} stakes`, stakes);
      
      const stake = stakes.find(s => s.stakeIndex === stakeIndex);
      
      if (!stake) {
        throw new Error(`Stake not found with index ${stakeIndex}`);
      }
      
      console.log(`Found stake:`, stake);
      
      // Get the min and max penalty rates
      const minPenaltyRate = await this.contract.minPenaltyRate();
      const maxPenaltyRate = await this.contract.maxPenaltyRate();
      
      console.log(`Penalty rates from contract: min=${minPenaltyRate.toString()}, max=${maxPenaltyRate.toString()}`);
      
      // Calculate remaining time and original duration
      const now = Math.floor(Date.now() / 1000);
      const remainingTime = stake.unlockTime - now;
      const originalLockDuration = stake.lockDuration;
      
      console.log(`Time calculation: now=${now}, unlockTime=${stake.unlockTime}, remainingTime=${remainingTime}, originalDuration=${originalLockDuration}`);
      
      if (remainingTime <= 0) {
        return {
          penaltyAmount: ethers.constants.Zero,
          formattedPenaltyAmount: '0',
          penaltyRate: 0,
          canUnstakeNormally: true
        };
      }
      
      // Calculate penalty rate - Same formula as in the contract but using BigNumber for safety
      // Convert all values to BigNumber to avoid precision issues
      const remaining = ethers.BigNumber.from(remainingTime.toString());
      const original = ethers.BigNumber.from(originalLockDuration.toString());
      const minRate = ethers.BigNumber.from(minPenaltyRate);
      const maxRate = ethers.BigNumber.from(maxPenaltyRate);
      
      // Calculate the penalty rate using BigNumber arithmetic
      let penaltyRate;
      if (original.isZero()) {
        penaltyRate = maxRate;
      } else {
        // Calculate: maxRate - (((maxRate - minRate) * (original - remaining)) / original)
        const rateRange = maxRate.sub(minRate);
        const timeProgress = original.sub(remaining);
        const reduction = rateRange.mul(timeProgress).div(original);
        penaltyRate = maxRate.sub(reduction);
        
        console.log(`Penalty calculation: maxRate=${maxRate.toString()}, minRate=${minRate.toString()}, 
          rateRange=${rateRange.toString()}, timeProgress=${timeProgress.toString()}, 
          reduction=${reduction.toString()}, finalRate=${penaltyRate.toString()}`);
      }
      
      // Calculate penalty amount
      const penaltyAmount = stake.amount.mul(penaltyRate).div(10000);
      const returnAmount = stake.amount.sub(penaltyAmount);
      
      console.log(`Final amounts: penalty=${ethers.utils.formatEther(penaltyAmount)}, 
        return=${ethers.utils.formatEther(returnAmount)}, 
        penaltyRate=${penaltyRate.toString()}`);
      
      return {
        penaltyAmount,
        formattedPenaltyAmount: ethers.utils.formatEther(penaltyAmount),
        returnAmount,
        formattedReturnAmount: ethers.utils.formatEther(returnAmount),
        penaltyRate: penaltyRate.toNumber() / 100, // Convert basis points to percentage
        rawPenaltyRate: penaltyRate.toString(), // Raw rate for fallback
        remainingTime,
        formattedRemainingTime: this.formatLockDuration(remainingTime),
        canUnstakeNormally: false
      };
    } catch (error) {
      console.error('Error calculating early unstake penalty:', error);
      throw error;
    }
  }
}

export default ProjectStakingService; 